import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, Loader2, Trash2, User, Mail, Phone, Euro, Calendar, Target, ArrowRightCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR, fmtDateShort } from "../lib/format";
import { Link } from "react-router-dom";

const STAGE_COLORS = {
  lead: "bg-neutral-100 border-neutral-200 text-neutral-700",
  sopralluogo: "bg-blue-50 border-blue-200 text-blue-700",
  preventivo_inviato: "bg-amber-50 border-amber-200 text-amber-700",
  negoziazione: "bg-orange-50 border-orange-200 text-orange-700",
  vinto: "bg-emerald-50 border-emerald-200 text-emerald-700",
  perso: "bg-neutral-100 border-neutral-200 text-neutral-500",
};

export default function Opportunities() {
  const [opps, setOpps] = useState([]);
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, o, c] = await Promise.all([
        api.get("/opportunities/meta"),
        api.get("/opportunities"),
        api.get("/clients"),
      ]);
      setMeta(m.data);
      setOpps(o.data);
      setClients(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    if (!meta) return {};
    const by = Object.fromEntries(meta.stages.map((s) => [s.key, []]));
    opps.forEach((o) => { (by[o.stage] || by.lead).push(o); });
    return by;
  }, [opps, meta]);

  const changeStage = async (id, stage) => {
    try {
      await api.patch(`/opportunities/${id}/stage`, { stage });
      setOpps((prev) => prev.map((o) => o.id === id ? { ...o, stage } : o));
    } catch { toast.error("Errore spostamento"); }
  };

  const openNew = () => { setEditing({ title: "", stage: "lead", kind: "incarico_privato", estimated_value: 0, probability: 50 }); setEditOpen(true); };
  const openEdit = (o) => { setEditing({ ...o }); setEditOpen(true); };

  const save = async () => {
    if (!editing?.title?.trim()) { toast.error("Titolo obbligatorio"); return; }
    try {
      const payload = { ...editing };
      // Remove nulls that would fail validation
      ["contact_email"].forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editing.id) {
        await api.put(`/opportunities/${editing.id}`, payload);
      } else {
        await api.post("/opportunities", payload);
      }
      toast.success("Salvato");
      setEditOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'opportunità?")) return;
    await api.delete(`/opportunities/${id}`);
    await load();
  };

  const convert = async (o) => {
    let client_type = null;
    if (!o.client_id) {
      const choice = window.prompt("Il cliente non esiste ancora. Che tipo è? Scrivi 'pa' o 'private'", "private");
      if (!choice) return;
      client_type = choice.trim().toLowerCase();
      if (!["pa", "private"].includes(client_type)) { toast.error("Tipo non valido"); return; }
    }
    try {
      const r = await api.post(`/opportunities/${o.id}/convert`, { client_type });
      toast.success("Opportunità convertita in progetto");
      await load();
      window.open(`/projects/${r.data.project_id}`, "_self");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore conversione"); }
  };

  if (loading || !meta) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const totalValue = opps.filter((o) => !["vinto", "perso"].includes(o.stage)).reduce((s, o) => s + (o.estimated_value || 0), 0);

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label flex items-center gap-1.5"><Target className="h-3 w-3" /> CRM commerciale</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Opportunità</h1>
          <p className="text-sm text-neutral-500 mt-2">
            Pipeline commerciale: <strong className="font-mono">{opps.length}</strong> opportunità ·
            Valore in gioco <strong className="font-mono brand-text">{fmtEUR(totalValue)}</strong>
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-opp-btn" className="brand-bg hover:opacity-90"><Plus className="h-4 w-4 mr-1.5" /> Nuova opportunità</Button>
      </header>

      {/* Kanban */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="opp-kanban">
        {meta.stages.map((s) => (
          <div
            key={s.key}
            data-testid={`col-${s.key}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragId) { changeStage(dragId, s.key); setDragId(null); } }}
            className="bg-white border border-neutral-200 flex flex-col min-h-96"
          >
            <div className={`px-3 py-2 border-b ${STAGE_COLORS[s.key]} flex items-center justify-between`}>
              <div className="font-display font-medium text-sm">{s.label}</div>
              <div className="font-mono text-[10px] font-semibold">{grouped[s.key]?.length ?? 0}</div>
            </div>
            <div className="flex-1 p-2 space-y-2 overflow-y-auto">
              {(grouped[s.key] || []).length === 0 && <div className="text-center text-[10px] text-neutral-400 py-6">—</div>}
              {(grouped[s.key] || []).map((o) => (
                <div
                  key={o.id}
                  draggable
                  onDragStart={() => setDragId(o.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => openEdit(o)}
                  data-testid={`opp-card-${o.id}`}
                  className="border border-neutral-200 p-2.5 bg-white hover:border-neutral-800 cursor-pointer group"
                >
                  <div className="text-sm font-medium text-neutral-900 line-clamp-2">{o.title}</div>
                  <div className="mono-label mt-1 truncate">{o.client_name}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="font-mono text-[11px] font-semibold brand-text">{fmtEUR(o.estimated_value)}</span>
                    {o.probability != null && <span className="font-mono text-[9px] text-neutral-500">{o.probability}%</span>}
                  </div>
                  {o.expected_close && <div className="mono-label mt-1 flex items-center gap-1"><Calendar className="h-2.5 w-2.5" /> {fmtDateShort(o.expected_close)}</div>}
                  <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    {!o.converted_project_id ? (
                      <button onClick={(e) => { e.stopPropagation(); convert(o); }} data-testid={`convert-${o.id}`} title="Converti in progetto"
                        className="text-[10px] px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1">
                        <ArrowRightCircle className="h-2.5 w-2.5" /> Converti
                      </button>
                    ) : (
                      <Link to={`/projects/${o.converted_project_id}`} onClick={(e) => e.stopPropagation()} className="text-[10px] px-1.5 py-0.5 border border-neutral-200 bg-neutral-50 text-neutral-600 flex items-center gap-1">
                        <FileText className="h-2.5 w-2.5" /> Progetto
                      </Link>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); remove(o.id); }} data-testid={`del-${o.id}`} className="text-[10px] px-1.5 py-0.5 border border-neutral-200 text-neutral-500 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-2.5 w-2.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Edit/Create dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica opportunità" : "Nuova opportunità"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="mono-label">Titolo</label>
                <Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="opp-title" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Stage</label>
                  <select value={editing.stage} onChange={(e) => setEditing({ ...editing, stage: e.target.value })} data-testid="opp-stage" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    {meta.stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Tipologia progetto</label>
                  <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} data-testid="opp-kind" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    {meta.kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Cliente esistente (opzionale)</label>
                  <select value={editing.client_id || ""} onChange={(e) => setEditing({ ...editing, client_id: e.target.value || null })} className="w-full border border-neutral-300 px-3 py-2 text-sm" data-testid="opp-client">
                    <option value="">— nessuno (uso i contatti sotto) —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Fonte</label>
                  <Input value={editing.source || ""} onChange={(e) => setEditing({ ...editing, source: e.target.value })} placeholder="passaparola, sito, LinkedIn…" />
                </div>
              </div>
              {!editing.client_id && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="mono-label flex items-center gap-1"><User className="h-3 w-3" /> Nome contatto</label>
                    <Input value={editing.contact_name || ""} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="mono-label flex items-center gap-1"><Mail className="h-3 w-3" /> Email</label>
                    <Input type="email" value={editing.contact_email || ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} />
                  </div>
                  <div>
                    <label className="mono-label flex items-center gap-1"><Phone className="h-3 w-3" /> Telefono</label>
                    <Input value={editing.contact_phone || ""} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mono-label flex items-center gap-1"><Euro className="h-3 w-3" /> Valore stimato</label>
                  <Input type="number" value={editing.estimated_value || 0} onChange={(e) => setEditing({ ...editing, estimated_value: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="mono-label">Probabilità (%)</label>
                  <Input type="number" min="0" max="100" value={editing.probability ?? 50} onChange={(e) => setEditing({ ...editing, probability: parseInt(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="mono-label">Chiusura attesa</label>
                  <Input type="date" value={editing.expected_close ? editing.expected_close.slice(0, 10) : ""} onChange={(e) => setEditing({ ...editing, expected_close: e.target.value || null })} />
                </div>
              </div>
              <div>
                <label className="mono-label">Descrizione / note</label>
                <Textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="opp-save" className="brand-bg hover:opacity-90">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
